import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { trackEvent } from '../utils/analytics';
import LandlordSettings from './LandlordSettings';
import InfoChip from './InfoChip';

const formatCurrency = (value) => {
  const num = Number(value || 0);
  return num.toLocaleString('en-CA', { style: 'currency', currency: 'CAD' });
};

function RentLedger({ initialTab = 'ledger' }) {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(initialTab);
  const [ledgers, setLedgers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentsError, setPaymentsError] = useState('');

  const [paymentsStatusFilter, setPaymentsStatusFilter] = useState('all');
  const [paymentsMethodFilter, setPaymentsMethodFilter] = useState('all');
  const [paymentsSearch, setPaymentsSearch] = useState('');
  const [refundProcessingId, setRefundProcessingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [amount, setAmount] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [activeTenantId, setActiveTenantId] = useState('');
  const [activeTenants, setActiveTenants] = useState([]);
  const [ledgerTab, setLedgerTab] = useState('due');
  const [expandedGroupId, setExpandedGroupId] = useState(null);
  const [menuGroupId, setMenuGroupId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterOverdue, setFilterOverdue] = useState(false);
  const [filterAutopay, setFilterAutopay] = useState(false);
  const [lastViewedAt, setLastViewedAt] = useState(null);
  const [ledgerUnread, setLedgerUnread] = useState(0);
  const [hideNewItems, setHideNewItems] = useState(localStorage.getItem('dismiss_newitems_landlord_ledger') === '1');
  const [inlineFormId, setInlineFormId] = useState(null);
  const [inlineFormType, setInlineFormType] = useState(null);
  const [inlineFormNote, setInlineFormNote] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab === 'settings') {
      setActiveTab('settings');
    } else if (tab === 'payments') {
      setActiveTab('payments');
    } else if (tab === 'ledger') {
      setActiveTab('ledger');
    }
  }, [location.search]);

  useEffect(() => {
    setExpandedGroupId(null);
    setMenuGroupId(null);
  }, [ledgerTab]);

  useEffect(() => {
    const fetchLedgers = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        setPaymentsLoading(true);
        setPaymentsError('');
        const [ledgerRes, tenantsRes, countRes, paymentsRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/rent-ledger`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/active-tenants`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/ledger/unread-count`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/payments`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
        const ledgerData = await ledgerRes.json().catch(() => ({}));
        const tenantsData = tenantsRes.ok ? await tenantsRes.json().catch(() => ([])) : [];
        const paymentsData = paymentsRes.ok ? await paymentsRes.json().catch(() => ({})) : { payments: [] };
        if (countRes.ok) {
          const countData = await countRes.json().catch(() => ({}));
          setLedgerUnread(countData.count || 0);
          setLastViewedAt(countData.last_viewed_at || null);
        }
        if (!ledgerRes.ok) throw new Error(ledgerData.error || 'Failed to fetch rent ledger');
        setLedgers(ledgerData.ledgers || []);
        setActiveTenants(Array.isArray(tenantsData) ? tenantsData : []);
        if (paymentsRes.ok) {
          setPayments(paymentsData.payments || []);
        } else {
          setPaymentsError(paymentsData.error || 'Failed to fetch payments');
        }

        await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/ledger/mark-viewed`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        });
        window.dispatchEvent(new Event('ledger:viewed'));
      } catch (err) {
        setError(err.message || 'Something went wrong');
      } finally {
        setLoading(false);
        setPaymentsLoading(false);
      }
    };

    fetchLedgers();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    const formData = new FormData(e.target);
    const chargeType = formData.get('charge_type') || 'rent';
    const note = formData.get('note') || '';
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/rent-ledger`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ activeTenantId, amount, dueDate, charge_type: chargeType, note })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to create ledger entry');
      setLedgers(prev => [data.ledger, ...prev]);
      setAmount('');
      setDueDate('');
      setActiveTenantId('');

      trackEvent({
        event_name: 'rent_ledger_created',
        event_category: 'rent',
        event_action: 'create',
        metadata: { amount, due_date: dueDate, active_tenant_id: activeTenantId }
      });
    } catch (err) {
      setError(err.message || 'Something went wrong');
    }
  };

  if (loading) return <main className="App-main">Loading rent ledger...</main>;
  if (error) return <main className="App-main"><p className="error-message">{error}</p></main>;

  const dueEntries = ledgers.filter(entry => ['due', 'late', 'processing', 'partial', 'pending_confirmation'].includes(entry.status));
  const historyEntries = ledgers.filter(entry => ['paid', 'failed', 'cancelled'].includes(entry.status));

  const normalizedPaymentsSearch = paymentsSearch.toLowerCase().trim();
  const filteredPayments = payments.filter((payment) => {
    const statusValue = payment.status || '';
    const methodValue = (payment.payment_method_type || '').toLowerCase();
    const methodMatch = paymentsMethodFilter === 'all'
      || (paymentsMethodFilter === 'card' && methodValue === 'card')
      || (paymentsMethodFilter === 'pad' && (methodValue === 'pad' || methodValue === 'acss_debit'));
    const statusMatch = paymentsStatusFilter === 'all'
      || (paymentsStatusFilter === 'refunded' && (statusValue === 'refunded' || statusValue === 'partially_refunded'))
      || (paymentsStatusFilter === 'disputed' && (statusValue === 'disputed' || !!payment.dispute_status))
      || statusValue === paymentsStatusFilter;
    const searchMatch = !normalizedPaymentsSearch
      || (payment.tenant_email || '').toLowerCase().includes(normalizedPaymentsSearch)
      || (payment.property_address || '').toLowerCase().includes(normalizedPaymentsSearch);
    return methodMatch && statusMatch && searchMatch;
  });

  const handleExportCsv = () => {
    const token = localStorage.getItem('token');
    const url = `${process.env.REACT_APP_API_BASE_URL}/api/landlords/rent-ledger/export.csv`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (!r.ok) throw new Error('Export failed'); return r.blob(); })
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `rent-ledger-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => alert('Unable to export ledger'));
  };

  const handlePaymentsExportCsv = () => {
    const token = localStorage.getItem('token');
    const url = `${process.env.REACT_APP_API_BASE_URL}/api/landlords/payments/export.csv`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (!r.ok) throw new Error('Export failed'); return r.blob(); })
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `payments-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => alert('Unable to export payments'));
  };

  const formatDate = (value) => {
    if (!value) return '—';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleDateString();
  };

  const copyToClipboard = (value) => {
    if (!value) return;
    navigator.clipboard?.writeText(value).catch(() => {});
  };

  const handleRefund = async (payment) => {
    if (!payment?.id) return;
    const amountInput = window.prompt('Refund amount (leave blank for full refund):', '');
    if (amountInput === null) return;
    const amountValue = amountInput.trim() === '' ? null : Number(amountInput);
    if (amountValue != null && !Number.isFinite(amountValue)) {
      setPaymentsError('Invalid refund amount');
      return;
    }
    const reasonInput = window.prompt('Refund reason (optional: duplicate, fraudulent, requested_by_customer):', 'requested_by_customer');
    const reasonValue = reasonInput ? reasonInput.trim() : null;

    setRefundProcessingId(payment.id);
    setPaymentsError('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/payments/${payment.id}/refund`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ amount: amountValue, reason: reasonValue || null })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Refund failed');
      const refund = data.refund || {};
      setPayments(prev => prev.map(p => p.id === payment.id ? {
        ...p,
        refund_status: refund.status || p.refund_status,
        refund_amount: refund.amount || p.refund_amount,
        status: refund.status === 'succeeded' ? 'refunded' : p.status
      } : p));
    } catch (err) {
      setPaymentsError(err.message || 'Refund failed');
    } finally {
      setRefundProcessingId(null);
    }
  };

  const groupLedgers = (entries, mode = 'due') => {
    const groups = new Map();
    entries.forEach((entry) => {
      const key = `${entry.property_id || 'prop'}-${entry.tenant_id || 'tenant'}`;
      if (!groups.has(key)) {
        groups.set(key, {
          key,
          tenant: entry.tenant_email || 'Tenant',
          property: entry.property_address || 'Property',
          entries: [],
          total: 0
        });
      }
      const group = groups.get(key);
      group.entries.push(entry);
      group.total += Number(entry.amount || 0);
    });

    const groupArray = Array.from(groups.values()).map((group) => {
      const sortedEntries = [...group.entries].sort((a, b) => {
        const first = new Date(a.due_date || a.created_at || 0).getTime();
        const second = new Date(b.due_date || b.created_at || 0).getTime();
        return mode === 'due' ? first - second : second - first;
      });
      return { ...group, entries: sortedEntries, nextEntry: sortedEntries[0] };
    });

    return groupArray.sort((a, b) => {
      if (mode === 'due') {
        return new Date(a.nextEntry?.due_date || 0) - new Date(b.nextEntry?.due_date || 0);
      }
      return new Date(b.nextEntry?.paid_at || b.nextEntry?.updated_at || 0) - new Date(a.nextEntry?.paid_at || a.nextEntry?.updated_at || 0);
    });
  };

  const latestLedgers = [...ledgers]
    .sort((a, b) => new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0))
    .slice(0, 3);

  const balanceDue = dueEntries.reduce((sum, entry) => sum + Number(entry.amount || 0), 0);
  const overdueCount = dueEntries.filter(entry => entry.status === 'late').length;
  const nextDue = [...dueEntries]
    .sort((a, b) => new Date(a.due_date || 0) - new Date(b.due_date || 0))
    .find(entry => entry.due_date);
  const groupedDue = groupLedgers(dueEntries, 'due');
  const groupedHistory = groupLedgers(historyEntries, 'history');

  const applyFilters = (groups, mode = 'due') => {
    const lowerSearch = searchTerm.trim().toLowerCase();
    return groups.filter((group) => {
      if (filterOverdue && mode === 'due' && !group.entries.some((entry) => entry.status === 'late')) {
        return false;
      }
      if (filterAutopay && !group.entries.some((entry) => entry.autopay_enabled)) {
        return false;
      }
      if (!lowerSearch) return true;
      return (
        group.tenant?.toLowerCase().includes(lowerSearch) ||
        group.property?.toLowerCase().includes(lowerSearch)
      );
    });
  };

  const filteredDue = applyFilters(groupedDue, 'due');
  const filteredHistory = applyFilters(groupedHistory, 'history');

  return (
    <main className="App-main">
      <div className="ledger-header">
        <div>
          <p className="eyebrow-tag">Payments</p>
          <h2>Payments {ledgerUnread > 0 && <span className="nav-badge">{ledgerUnread}</span>}</h2>
          <p>Track rent status, due dates, and payments across tenants.</p>
        </div>
      </div>

      <div className="subnav-wrap" style={{ marginTop: '1.5rem' }}>
        <div className="analytics-subnav">
          <button className={activeTab === 'ledger' ? 'primary-button' : 'secondary-button'} onClick={() => setActiveTab('ledger')}>Ledger</button>
          <button className={activeTab === 'payments' ? 'primary-button' : 'secondary-button'} onClick={() => setActiveTab('payments')}>Payments</button>
          <button className={activeTab === 'settings' ? 'primary-button' : 'secondary-button'} onClick={() => setActiveTab('settings')}>Settings</button>
        </div>
      </div>

      {activeTab === 'ledger' && (
        <>
      {!hideNewItems && ledgerUnread > 0 && latestLedgers.length > 0 && (
        <div className="dashboard-section new-items-card" style={{ borderLeft: '4px solid #2f6fed', background: 'var(--surface-color, #f5f8ff)' }}>
          <div className="section-header">
            <h3>New items</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span className="report-chip report-chip--muted">Latest activity</span>
              <button
                className="card-dismiss"
                onClick={() => { localStorage.setItem('dismiss_newitems_landlord_ledger', '1'); setHideNewItems(true); }}
                aria-label="Dismiss"
              >
                Dismiss
              </button>
            </div>
          </div>
        {latestLedgers.length === 0 ? (
          <p className="status-text">No ledger activity yet.</p>
        ) : (
          <div className="inquiry-list">
            {latestLedgers.map((entry) => {
              const updatedAt = entry.updated_at || entry.created_at;
              const isNew = lastViewedAt && updatedAt && new Date(updatedAt) > new Date(lastViewedAt);
              return (
                <div key={`latest-${entry.id}`} className="inquiry-card">
                  <p><strong>{formatCurrency(entry.amount)}</strong> • {entry.tenant_email || 'Tenant'}{entry.unit_label ? ` (Unit ${entry.unit_label})` : ''}</p>
                  <p className="status-text">Status: {entry.status}</p>
                  <p className="timestamp">{updatedAt ? new Date(updatedAt).toLocaleString() : '—'}</p>
                  <div className="form-actions" style={{ marginTop: '0.5rem' }}>
                    <a className="secondary-button" href={`#ledger-${entry.id}`}>View entry</a>
                    {isNew && <span className="report-chip" style={{ marginLeft: '0.5rem' }}>New</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      )}

      <div className="ledger-summary-bar" style={{ marginTop: '1rem', borderRadius: '12px', border: '1px solid var(--border-color, #e4e4e7)', padding: '0.85rem 1.25rem', display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
        <div style={{ minWidth: '160px' }}>
          <p className="status-text" style={{ margin: 0 }}>Balance due</p>
          <strong>{formatCurrency(balanceDue)}</strong>
        </div>
        <div style={{ minWidth: '200px' }}>
          <p className="status-text" style={{ margin: 0 }}>Next due</p>
          <strong>{nextDue ? `${formatDate(nextDue.due_date)} · ${formatCurrency(nextDue.amount)}` : '—'}</strong>
        </div>
        <div style={{ minWidth: '120px' }}>
          <p className="status-text" style={{ margin: 0 }}>Overdue</p>
          <strong className={overdueCount === 0 ? 'muted' : ''}>{overdueCount}</strong>
        </div>
      </div>

      <div className="ledger-filter-bar" style={{ marginTop: '1rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <InfoChip label="Guide">
          Use search + filters to focus on overdue tenants, then expand a card to view invoices or receipts.
        </InfoChip>
        <input
          type="search"
          className="form-control"
          placeholder="Search tenant or property"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ flex: '1 1 260px', minWidth: '220px' }}
        />
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button
            type="button"
            className={`chip-button ${filterOverdue ? 'chip-button--active' : ''}`}
            onClick={() => setFilterOverdue(!filterOverdue)}
          >
            Overdue only
          </button>
          <button
            type="button"
            className={`chip-button ${filterAutopay ? 'chip-button--active' : ''}`}
            onClick={() => setFilterAutopay(!filterAutopay)}
          >
            Autopay only
          </button>
        </div>
      </div>

      <div className="subnav-wrap" style={{ marginTop: '1.25rem' }}>
        <div className="analytics-subnav">
          <button className={ledgerTab === 'due' ? 'primary-button' : 'secondary-button'} onClick={() => setLedgerTab('due')}>Due now</button>
          <button className={ledgerTab === 'history' ? 'primary-button' : 'secondary-button'} onClick={() => setLedgerTab('history')}>History</button>
        </div>
      </div>

      {ledgerTab === 'due' && (
        <>
          {filteredDue.length === 0 ? (
            <p className="status-text">No outstanding payments.</p>
          ) : (
            <div className="ledger-grid">
              {filteredDue.map((group) => (
                <div key={group.key} className="ledger-item" id={`group-${group.key}`}>
                  <div className="ledger-item-header" style={{ justifyContent: 'space-between' }}>
                    <div>
                      <h3>{formatCurrency(group.total)}</h3>
                      <p className="status-text" style={{ margin: 0 }}>{group.tenant}</p>
                      <p className="status-text" style={{ margin: 0 }}>{group.property}</p>
                    </div>
                    <div style={{ textAlign: 'right', position: 'relative' }}>
                      <span className={`report-chip ${group.nextEntry?.status || 'due'}`}>
                        {group.nextEntry?.status || 'due'}
                      </span>
                      <p className="status-text" style={{ marginTop: '0.35rem' }}>
                        Next due: {formatDate(group.nextEntry?.due_date)}
                      </p>
                      <p className="status-text" style={{ margin: 0 }}>{group.entries.length} invoice{group.entries.length > 1 ? 's' : ''}</p>
                      <button
                        className="ghost-button"
                        style={{ padding: 0, marginTop: '0.35rem' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setMenuGroupId(menuGroupId === group.key ? null : group.key);
                        }}
                      >
                        •••
                      </button>
                      {menuGroupId === group.key && (
                        <div className="ledger-menu" style={{ position: 'absolute', right: 0, top: '2rem', background: 'var(--surface-color, #fff)', border: '1px solid var(--border-color, #e4e4e7)', borderRadius: '8px', boxShadow: '0 8px 20px rgba(15,23,42,0.15)', zIndex: 10 }}>
                          <button className="ghost-button" style={{ width: '100%', textAlign: 'left', padding: '0.5rem 0.75rem' }} onClick={() => { setExpandedGroupId(group.key); setMenuGroupId(null); }}>
                            View invoices
                          </button>
                          <button className="ghost-button" style={{ width: '100%', textAlign: 'left', padding: '0.5rem 0.75rem' }} onClick={() => { copyToClipboard(group.tenant); setMenuGroupId(null); }}>
                            Copy tenant email
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="form-actions" style={{ marginTop: '0.75rem' }}>
                    <button
                      className="ghost-button"
                      onClick={() => setExpandedGroupId(expandedGroupId === group.key ? null : group.key)}
                    >
                      {expandedGroupId === group.key ? 'Hide invoices' : 'View invoices'}
                    </button>
                  </div>
                  {expandedGroupId === group.key && (
                    <div className="maintenance-details" style={{ marginTop: '0.75rem' }}>
                      {group.entries.map((entry) => (
                        <div key={entry.id} id={`ledger-${entry.id}`} style={{ padding: '0.5rem 0', borderTop: '1px solid var(--border-color, #e4e4e7)' }}>
                          <p className="status-text"><strong>Due:</strong> {formatDate(entry.due_date)} · {formatCurrency(entry.amount)}</p>
                          <p className="status-text"><strong>Status:</strong> {entry.status}</p>
                          {entry.status === 'partial' && (
                            <p className="status-text"><strong>Paid:</strong> {formatCurrency(entry.amount_paid || 0)} · <strong>Remaining:</strong> {formatCurrency(Number(entry.amount || 0) - Number(entry.amount_paid || 0))}</p>
                          )}
                          {entry.late_fee_applied && Number(entry.late_fee_amount || 0) > 0 && (
                            <p className="status-text" style={{ color: '#ef4444' }}><strong>Late fee:</strong> {formatCurrency(entry.late_fee_amount)}</p>
                          )}
                          {entry.charge_type && entry.charge_type !== 'rent' && (
                            <p className="status-text"><strong>Type:</strong> {entry.charge_type.replace('_', ' ')}</p>
                          )}
                          {entry.approval_status === 'pending' && (
                            <p className="status-text" style={{ color: '#f5a623' }}><strong>⏳ Awaiting tenant approval</strong></p>
                          )}
                          {entry.approval_status === 'disputed' && (
                            <div>
                              <p className="status-text" style={{ color: '#ef4444' }}><strong>⚠️ Disputed by tenant</strong></p>
                              {entry.dispute_note && <p className="status-text">Reason: {entry.dispute_note}</p>}
                              <div className="form-actions" style={{ marginTop: '0.35rem', gap: '0.35rem' }}>
                                {inlineFormId === entry.id && inlineFormType === 'approve' ? (
                                  <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'center', flexWrap: 'wrap' }}>
                                    <input
                                      type="text"
                                      className="form-control"
                                      placeholder="Note (optional)"
                                      value={inlineFormNote}
                                      onChange={(e) => setInlineFormNote(e.target.value)}
                                      style={{ maxWidth: '200px', fontSize: '0.8rem' }}
                                    />
                                    <button
                                      className="primary-button"
                                      style={{ fontSize: '0.8rem', padding: '0.3rem 0.75rem' }}
                                      onClick={async () => {
                                        const token = localStorage.getItem('token');
                                        const r = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/rent-ledger/${entry.id}/resolve`, {
                                          method: 'POST',
                                          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                                          body: JSON.stringify({ action: 'approve', note: inlineFormNote || undefined })
                                        });
                                        if (r.ok) { const d = await r.json().catch(() => ({})); if (d.ledger) setLedgers(prev => prev.map(l => l.id === d.ledger.id ? d.ledger : l).filter(l => l.status !== 'cancelled')); }
                                        else { const errData = await r.json().catch(() => ({})); setError(errData.error || 'Error'); }
                                        setInlineFormId(null); setInlineFormType(null); setInlineFormNote('');
                                      }}
                                    >Confirm</button>
                                    <button
                                      className="ghost-button"
                                      style={{ fontSize: '0.8rem', padding: '0.3rem 0.75rem' }}
                                      onClick={() => { setInlineFormId(null); setInlineFormType(null); setInlineFormNote(''); }}
                                    >Cancel</button>
                                  </div>
                                ) : (
                                  <button
                                    className="primary-button"
                                    style={{ fontSize: '0.8rem', padding: '0.3rem 0.75rem' }}
                                    onClick={() => { setInlineFormId(entry.id); setInlineFormType('approve'); setInlineFormNote(''); }}
                                  >Approve anyway</button>
                                )}
                                <button
                                  className="ghost-button"
                                  style={{ fontSize: '0.8rem', padding: '0.3rem 0.75rem', color: '#6b7280' }}
                                  onClick={async () => {
                                    if (!window.confirm('Withdraw this charge? It will be cancelled.')) return;
                                    const token = localStorage.getItem('token');
                                    const r = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/rent-ledger/${entry.id}/resolve`, {
                                      method: 'POST',
                                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                                      body: JSON.stringify({ action: 'withdraw' })
                                    });
                                    if (r.ok) { const d = await r.json().catch(() => ({})); if (d.ledger) setLedgers(prev => prev.map(l => l.id === d.ledger.id ? d.ledger : l).filter(l => l.status !== 'cancelled')); }
                                    else alert((await r.json().catch(() => ({}))).error || 'Error');
                                  }}
                                >Withdraw charge</button>
                              </div>
                            </div>
                          )}
                          {entry.approval_status === 'pending' && entry.charge_type !== 'rent' && (
                            <div>
                              <p className="status-text" style={{ color: '#d97706' }}><strong>⏳ Pending tenant approval</strong></p>
                              <button
                                className="ghost-button"
                                style={{ fontSize: '0.8rem', padding: '0.3rem 0.75rem', marginTop: '0.25rem', color: '#6b7280' }}
                                onClick={async () => {
                                  if (!window.confirm('Withdraw this charge before the tenant responds?')) return;
                                  const token = localStorage.getItem('token');
                                  const r = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/rent-ledger/${entry.id}/resolve`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                                    body: JSON.stringify({ action: 'withdraw' })
                                  });
                                  if (r.ok) { const d = await r.json().catch(() => ({})); if (d.ledger) setLedgers(prev => prev.map(l => l.id === d.ledger.id ? d.ledger : l).filter(l => l.status !== 'cancelled')); }
                                  else alert((await r.json().catch(() => ({}))).error || 'Error');
                                }}
                              >Withdraw charge</button>
                            </div>
                          )}
                          {entry.payment_status && <p className="status-text"><strong>Payment:</strong> {entry.payment_status}</p>}
                          {entry.status === 'pending_confirmation' && (
                            <div style={{ marginTop: '0.35rem', borderLeft: '4px solid #f59e0b', background: '#fffbeb', padding: '0.5rem 0.75rem', borderRadius: '6px' }}>
                              <p className="status-text" style={{ margin: '0 0 0.35rem', fontWeight: 500, color: '#92400e' }}>
                                ⏳ Tenant reported this as paid — confirm or reject
                              </p>
                              <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                                <button
                                  className="primary-button"
                                  style={{ fontSize: '0.8rem', padding: '0.3rem 0.75rem' }}
                                  onClick={async () => {
                                    const token = localStorage.getItem('token');
                                    try {
                                      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/rent-ledger/${entry.id}/confirm-payment`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                                        body: JSON.stringify({ action: 'confirm' })
                                      });
                                      const data = await res.json().catch(() => ({}));
                                      if (!res.ok) throw new Error(data.error || 'Failed');
                                      setLedgers(prev => prev.map(l => l.id === entry.id ? { ...l, status: 'paid', paid_at: new Date().toISOString() } : l));
                                    } catch (err) {
                                      setError(err.message || 'Failed to confirm payment');
                                    }
                                  }}
                                >✅ Confirm received</button>
                                <button
                                  className="ghost-button"
                                  style={{ fontSize: '0.8rem', padding: '0.3rem 0.75rem', color: '#dc2626' }}
                                  onClick={async () => {
                                    const token = localStorage.getItem('token');
                                    try {
                                      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/rent-ledger/${entry.id}/confirm-payment`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                                        body: JSON.stringify({ action: 'reject' })
                                      });
                                      const data = await res.json().catch(() => ({}));
                                      if (!res.ok) throw new Error(data.error || 'Failed');
                                      setLedgers(prev => prev.map(l => l.id === entry.id ? { ...l, status: 'due' } : l));
                                    } catch (err) {
                                      setError(err.message || 'Failed to reject payment');
                                    }
                                  }}
                                >❌ Not received</button>
                              </div>
                            </div>
                          )}
                          {entry.status !== 'paid' && entry.status !== 'pending_confirmation' && entry.approval_status !== 'pending' && entry.approval_status !== 'disputed' && (
                            inlineFormId === entry.id && inlineFormType === 'mark-paid' ? (
                              <div style={{ marginTop: '0.35rem', display: 'flex', gap: '0.35rem', alignItems: 'center', flexWrap: 'wrap' }}>
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder='Note (e.g., "Cash payment received")'
                                  value={inlineFormNote}
                                  onChange={(e) => setInlineFormNote(e.target.value)}
                                  style={{ maxWidth: '250px', fontSize: '0.8rem' }}
                                />
                                <button
                                  className="primary-button"
                                  style={{ fontSize: '0.8rem', padding: '0.3rem 0.75rem' }}
                                  onClick={async () => {
                                    const token = localStorage.getItem('token');
                                    try {
                                      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/rent-ledger/${entry.id}/mark-paid`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                                        body: JSON.stringify({ note: inlineFormNote || undefined })
                                      });
                                      const data = await res.json().catch(() => ({}));
                                      if (!res.ok) throw new Error(data.error || 'Failed');
                                      setLedgers(prev => prev.map(l => l.id === entry.id ? { ...l, status: 'paid', paid_at: new Date().toISOString() } : l));
                                    } catch (err) {
                                      setError(err.message || 'Failed to mark as paid');
                                    }
                                    setInlineFormId(null); setInlineFormType(null); setInlineFormNote('');
                                  }}
                                >Confirm</button>
                                <button
                                  className="ghost-button"
                                  style={{ fontSize: '0.8rem', padding: '0.3rem 0.75rem' }}
                                  onClick={() => { setInlineFormId(null); setInlineFormType(null); setInlineFormNote(''); }}
                                >Cancel</button>
                              </div>
                            ) : (
                              <button
                                className="secondary-button"
                                style={{ marginTop: '0.35rem' }}
                                onClick={() => { setInlineFormId(entry.id); setInlineFormType('mark-paid'); setInlineFormNote(''); }}
                              >
                                Mark as paid
                              </button>
                            )
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {ledgerTab === 'history' && (
        <>
          <div className="ledger-card" style={{ marginTop: '0.5rem' }}>
            <div className="ledger-item-header" style={{ justifyContent: 'space-between' }}>
              <h3>Export</h3>
              <button className="secondary-button" onClick={handleExportCsv}>Export CSV</button>
              {ledgers.length > 0 && (() => {
                const tenantIds = [...new Set(ledgers.map(l => l.tenant_id).filter(Boolean))];
                return tenantIds.length === 1 ? (
                  <button className="secondary-button" onClick={() => {
                    const token = localStorage.getItem('token');
                    const url = `${process.env.REACT_APP_API_BASE_URL}/api/landlords/payments/statement?tenant_id=${tenantIds[0]}&year=${new Date().getFullYear()}`;
                    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
                      .then(r => { if (!r.ok) throw new Error('Failed'); return r.blob(); })
                      .then(blob => { const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `rent-statement-${new Date().getFullYear()}.pdf`; a.click(); })
                      .catch(() => alert('Unable to download statement'));
                  }}>
                    Statement PDF
                  </button>
                ) : null;
              })()}
            </div>
          </div>

          {filteredHistory.length === 0 ? (
            <p className="status-text">No payment history yet.</p>
          ) : (
            <div className="ledger-grid">
              {filteredHistory.map((group) => (
                <div key={`history-${group.key}`} className="ledger-item">
                  <div className="ledger-item-header" style={{ justifyContent: 'space-between' }}>
                    <div>
                      <h3>{formatCurrency(group.total)}</h3>
                      <p className="status-text" style={{ margin: 0 }}>{group.tenant}</p>
                      <p className="status-text" style={{ margin: 0 }}>{group.property}</p>
                    </div>
                    <div style={{ textAlign: 'right', position: 'relative' }}>
                      <p className="status-text" style={{ margin: 0 }}>Most recent: {formatDate(group.nextEntry?.paid_at)}</p>
                      <p className="status-text" style={{ margin: 0 }}>{group.entries.length} payment{group.entries.length > 1 ? 's' : ''}</p>
                      <button
                        className="ghost-button"
                        style={{ padding: 0, marginTop: '0.35rem' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setMenuGroupId(menuGroupId === group.key ? null : group.key);
                        }}
                      >
                        •••
                      </button>
                      {menuGroupId === group.key && (
                        <div className="ledger-menu" style={{ position: 'absolute', right: 0, top: '2rem', background: 'var(--surface-color, #fff)', border: '1px solid var(--border-color, #e4e4e7)', borderRadius: '8px', boxShadow: '0 8px 20px rgba(15,23,42,0.15)', zIndex: 10 }}>
                          <button className="ghost-button" style={{ width: '100%', textAlign: 'left', padding: '0.5rem 0.75rem' }} onClick={() => { setExpandedGroupId(group.key); setMenuGroupId(null); }}>
                            View payments
                          </button>
                          {group.nextEntry?.payment_id && (
                            <a
                              className="ghost-button"
                              style={{ width: '100%', textAlign: 'left', padding: '0.5rem 0.75rem' }}
                              href={`/landlord/dashboard?payment_id=${group.nextEntry.payment_id}#payments`}
                            >
                              Latest receipt
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="form-actions" style={{ marginTop: '0.75rem' }}>
                    <button
                      className="ghost-button"
                      onClick={() => setExpandedGroupId(expandedGroupId === group.key ? null : group.key)}
                    >
                      {expandedGroupId === group.key ? 'Hide payments' : 'View payments'}
                    </button>
                    {group.nextEntry?.payment_id && (
                      <a className="secondary-button" href={`/landlord/dashboard?payment_id=${group.nextEntry.payment_id}#payments`}>
                        Latest receipt
                      </a>
                    )}
                  </div>
                  {expandedGroupId === group.key && (
                    <div className="maintenance-details" style={{ marginTop: '0.75rem' }}>
                      {group.entries.map((entry) => (
                        <div key={entry.id} style={{ padding: '0.5rem 0', borderTop: '1px solid var(--border-color, #e4e4e7)' }}>
                          <p className="status-text"><strong>Paid:</strong> {formatDate(entry.paid_at)} · {formatCurrency(entry.amount)}</p>
                          {entry.payment_status && <p className="status-text"><strong>Payment:</strong> {entry.payment_status}</p>}
                          {entry.capture_status && <p className="status-text"><strong>Capture:</strong> {entry.capture_status}</p>}
                          {entry.preauth_status && <p className="status-text"><strong>Pre‑auth:</strong> {entry.preauth_status}</p>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="ledger-card" style={{ marginTop: '1rem' }}>
            <h3>Create a charge</h3>
            <p className="status-text">Rent charges are auto-collected. Other charge types require tenant approval before collection.</p>
            <form onSubmit={handleCreate} className="maintenance-form">
              <select value={activeTenantId} onChange={(e) => setActiveTenantId(e.target.value)} required>
                <option value="">Select tenant</option>
                {activeTenants.map(t => (
                  <option key={t.id} value={t.id}>
                    {t.tenant_name || t.tenant_email} — {t.property_address}{t.unit_label ? ` (Unit ${t.unit_label})` : ''}
                  </option>
                ))}
              </select>
              <select name="charge_type" defaultValue="rent" className="form-control">
                <option value="rent">Rent</option>
                <option value="late_fee">Late fee</option>
                <option value="repair">Repair / maintenance</option>
                <option value="utility">Utility</option>
                <option value="other">Other</option>
              </select>
              <input type="number" step="0.01" placeholder="Amount" value={amount} onChange={(e) => setAmount(e.target.value)} required />
              <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} required />
              <input type="text" name="note" placeholder="Note (visible to tenant)" className="form-control" />
              <button className="primary-button" type="submit">Create Charge</button>
            </form>
          </div>
        </>
      )}
        </>
      )}

      {activeTab === 'payments' && (
        <div className="dashboard-section" style={{ marginTop: '1rem' }}>
          <div className="section-header" style={{ alignItems: 'center' }}>
            <div>
              <h3>Payment history</h3>
              <p className="status-text">Review completed payments, disputes, and refunds.</p>
            </div>
            <button className="secondary-button" onClick={handlePaymentsExportCsv}>Export CSV</button>
          </div>

          <div className="ledger-filter-bar" style={{ marginTop: '1rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <label className="status-text">
              Status
              <select className="form-control" value={paymentsStatusFilter} onChange={(e) => setPaymentsStatusFilter(e.target.value)}>
                <option value="all">All</option>
                <option value="succeeded">Succeeded</option>
                <option value="processing">Processing</option>
                <option value="failed">Failed</option>
                <option value="disputed">Disputed</option>
                <option value="refunded">Refunded</option>
              </select>
            </label>
            <label className="status-text">
              Method
              <select className="form-control" value={paymentsMethodFilter} onChange={(e) => setPaymentsMethodFilter(e.target.value)}>
                <option value="all">All</option>
                <option value="card">Card</option>
                <option value="pad">PAD</option>
              </select>
            </label>
            <label className="status-text" style={{ flex: 1, minWidth: '200px' }}>
              Search
              <input
                className="form-control"
                value={paymentsSearch}
                onChange={(e) => setPaymentsSearch(e.target.value)}
                placeholder="Tenant email or property"
              />
            </label>
          </div>

          {!paymentsLoading && filteredPayments.length > 0 && (() => {
            const succeeded = filteredPayments.filter(p => p.status === 'succeeded');
            const totalCollected = succeeded.reduce((sum, p) => sum + Number(p.amount || 0), 0);
            const thisMonth = new Date().getMonth();
            const thisYear = new Date().getFullYear();
            const mtd = succeeded.filter(p => {
              if (!p.paid_at) return false;
              const d = new Date(p.paid_at);
              return d.getMonth() === thisMonth && d.getFullYear() === thisYear;
            });
            const mtdTotal = mtd.reduce((sum, p) => sum + Number(p.amount || 0), 0);
            return (
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
                <div className="dashboard-section" style={{ flex: '1 1 140px', padding: '0.6rem', textAlign: 'center', borderLeft: '3px solid #2f6fed' }}>
                  <p className="status-text" style={{ margin: 0, fontSize: '0.7rem' }}>Total Collected</p>
                  <p style={{ margin: '0.2rem 0 0', fontWeight: '700', fontSize: '1rem' }}>{formatCurrency(totalCollected)}</p>
                </div>
                <div className="dashboard-section" style={{ flex: '1 1 140px', padding: '0.6rem', textAlign: 'center', borderLeft: '3px solid #059669' }}>
                  <p className="status-text" style={{ margin: 0, fontSize: '0.7rem' }}>This Month</p>
                  <p style={{ margin: '0.2rem 0 0', fontWeight: '700', fontSize: '1rem' }}>{formatCurrency(mtdTotal)}</p>
                </div>
                <div className="dashboard-section" style={{ flex: '1 1 140px', padding: '0.6rem', textAlign: 'center', borderLeft: '3px solid #6b7280' }}>
                  <p className="status-text" style={{ margin: 0, fontSize: '0.7rem' }}>Payments</p>
                  <p style={{ margin: '0.2rem 0 0', fontWeight: '700', fontSize: '1rem' }}>{succeeded.length}</p>
                </div>
              </div>
            );
          })()}

          {paymentsError && <p className="error-message">{paymentsError}</p>}
          {paymentsLoading ? (
            <p className="status-text">Loading payments…</p>
          ) : filteredPayments.length === 0 ? (
            <p className="status-text">No payments match your filters.</p>
          ) : (
            <div className="ledger-grid" style={{ marginTop: '1rem' }}>
              {filteredPayments.map((payment) => {
                const amount = Number(payment.amount || 0);
                const platformFeeType = payment.platform_fee_type || (payment.platform_fee_pct != null ? 'percent' : 'percent');
                const platformFeeValue = payment.platform_fee_value ?? payment.platform_fee_pct ?? 0;
                const platformFeeAmount = platformFeeType === 'flat'
                  ? Number(platformFeeValue || 0)
                  : (amount * Number(platformFeeValue || 0)) / 100;
                const applicationFeeAmount = (payment.application_fee_amount || 0) / 100;
                const processingFeeAmount = Math.max(0, applicationFeeAmount - platformFeeAmount);
                const processingPassedThrough = !!payment.card_fee_paid_by_tenant;
                const platformPassedThrough = !!payment.pass_through_platform_fee;
                const netToLandlord = amount
                  - (platformPassedThrough ? 0 : platformFeeAmount)
                  - (processingPassedThrough ? 0 : processingFeeAmount);
                const statusLabel = (payment.status || 'unknown').replace('_', ' ');
                const hasDispute = payment.status === 'disputed' || payment.dispute_status;
                const hasRefund = payment.status === 'refunded' || payment.status === 'partially_refunded' || payment.refund_status;

                return (
                  <div key={payment.id} className="ledger-item">
                    <div className="ledger-item-header">
                      <h3>{formatCurrency(amount)}</h3>
                      <span className={`status-pill ${payment.status}`}>{statusLabel}</span>
                    </div>
                    <p><strong>Tenant:</strong> {payment.tenant_email || '—'}</p>
                    <p><strong>Property:</strong> {payment.property_address || '—'}</p>
                    <p><strong>Due:</strong> {formatDate(payment.due_date)}</p>
                    <p><strong>Paid:</strong> {formatDate(payment.paid_at)}</p>
                    <p><strong>Method:</strong> {payment.payment_method_type || '—'} {payment.payment_method_last4 ? `•••• ${payment.payment_method_last4}` : ''}</p>
                    {payment.failure_reason && <p className="error-message">Failure: {payment.failure_reason}</p>}

                    {hasDispute && (
                      <div className="dashboard-section" style={{ marginTop: '0.5rem', borderLeft: '4px solid #ef4444', background: 'var(--surface-color, #fff5f5)', padding: '0.75rem' }}>
                        <p className="status-text" style={{ margin: 0 }}>
                          Dispute {payment.dispute_status || ''} {payment.dispute_reason ? `· ${payment.dispute_reason}` : ''}
                        </p>
                      </div>
                    )}
                    {hasRefund && (
                      <div className="dashboard-section" style={{ marginTop: '0.5rem', borderLeft: '4px solid #16a34a', background: 'var(--surface-color, #f0fdf4)', padding: '0.75rem' }}>
                        <p className="status-text" style={{ margin: 0 }}>
                          Refund {payment.refund_status || ''}{payment.refund_amount ? ` · ${formatCurrency(payment.refund_amount / 100)}` : ''}
                        </p>
                      </div>
                    )}

                    <div className="dashboard-section" style={{ marginTop: '0.5rem', background: 'var(--surface-color, #f8fafc)', padding: '0.75rem', borderRadius: '10px' }}>
                      <p className="status-text" style={{ margin: 0 }}><strong>Fee breakdown</strong></p>
                      <p className="status-text" style={{ margin: 0 }}>Platform fee: {platformFeeType === 'flat' ? formatCurrency(platformFeeAmount) : `${platformFeeValue}%`} {platformPassedThrough ? '(passed through)' : '(paid by landlord)'}</p>
                      <p className="status-text" style={{ margin: 0 }}>Processing fee: {formatCurrency(processingFeeAmount)} {processingPassedThrough ? '(passed through)' : '(paid by landlord)'}</p>
                      <p className="status-text" style={{ margin: 0 }}>Estimated net to you: {formatCurrency(netToLandlord)}</p>
                    </div>

                    {Array.isArray(payment.fee_events) && payment.fee_events.length > 0 && (
                      <div className="status-text" style={{ marginTop: '0.5rem' }}>
                        <strong>Fee events:</strong>
                        <ul style={{ margin: '0.35rem 0 0', paddingLeft: '1.25rem' }}>
                          {payment.fee_events.map((fee) => (
                            <li key={fee.id || `${fee.fee_type}-${fee.created_at}`}>
                              {fee.fee_type === 'pad_failure_fee' ? 'PAD failure fee' : fee.fee_type} · {formatCurrency(fee.amount || 0)} · {fee.charged_to || 'n/a'}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div className="form-actions" style={{ marginTop: '0.75rem' }}>
                      <button
                        className="secondary-button"
                        onClick={() => handleRefund(payment)}
                        disabled={refundProcessingId === payment.id || payment.status !== 'succeeded'}
                      >
                        {refundProcessingId === payment.id ? 'Refunding…' : 'Issue refund'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {activeTab === 'settings' && (
        <LandlordSettings embedded />
      )}
    </main>
  );
}

export default RentLedger;
